import { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { RFIItem, RFIStatus, RFIPriority } from '@/types/rfiItem';
import { mockRFIItems } from '@/data/mockRFIItems';

export interface UploadedDocument {
  id: string;
  name: string;
  size: number;
  type: string;
  uploadedAt: Date;
  source: 'databook' | 'rfi-tracker';
}

export interface TransactionBackground {
  projectName: string;
  entityName: string;
  industry: string;
  jurisdictions: string[];
  legalEntities: string;
  dealType: string;
  focusAreas: string;
  pnlIncluded: boolean;
  pnlHistoricalFrom: string;
  pnlHistoricalTo: string;
  ytdMonth: string;
  includeForecast: boolean;
  pnlForecastFrom: string;
  pnlForecastTo: string;
  bsIncluded: boolean;
  bsHistoricalDifferent: boolean;
  bsHistoricalFrom: string;
  bsHistoricalTo: string;
  bsIncludeForecast: boolean;
  bsForecastFrom: string;
  bsForecastTo: string;
  documentsCount: number;
}

interface RFIContextType {
  rfiItems: RFIItem[];
  newItemIds: Set<string>;
  uploadedDocuments: UploadedDocument[];
  transactionBackground: TransactionBackground | null;
  addRFIItems: (items: RFIItem[]) => void;
  updateRFIStatus: (id: string, status: RFIStatus) => void;
  updateRFIComment: (id: string, comment: string) => void;
  addUploadedDocuments: (docs: UploadedDocument[]) => void;
  removeUploadedDocument: (id: string) => void;
  processUploadedFiles: (files: File[], source: 'databook' | 'rfi-tracker') => void;
  setTransactionBackground: (data: TransactionBackground) => void;
  markItemSeen: (id: string) => void;
}

const RFIContext = createContext<RFIContextType | undefined>(undefined);

export const useRFI = () => {
  const context = useContext(RFIContext);
  if (!context) {
    throw new Error('useRFI must be used within an RFIProvider');
  }
  return context;
};

export const RFIProvider = ({ children }: { children: ReactNode }) => {
  const [rfiItems, setRFIItems] = useState<RFIItem[]>(mockRFIItems);
  const [newItemIds, setNewItemIds] = useState<Set<string>>(new Set());
  const [uploadedDocuments, setUploadedDocuments] = useState<UploadedDocument[]>([]);
  const [transactionBackground, setTransactionBackgroundState] = useState<TransactionBackground | null>(null);

  const addRFIItems = useCallback((items: RFIItem[]) => {
    setRFIItems(prev => {
      const existingIds = new Set(prev.map(i => i.id));
      const newItems = items.filter(i => !existingIds.has(i.id));
      if (newItems.length > 0) {
        setNewItemIds(prevIds => {
          const next = new Set(prevIds);
          newItems.forEach(i => next.add(i.id));
          return next;
        });
      }
      return [...prev, ...newItems];
    });
  }, []);

  const markItemSeen = useCallback((id: string) => {
    setNewItemIds(prev => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const updateRFIStatus = useCallback((id: string, status: RFIStatus) => {
    setRFIItems(prev => prev.map(item => 
      item.id === id ? { ...item, status } : item
    ));
  }, []);

  const updateRFIComment = useCallback((id: string, comment: string) => {
    setRFIItems(prev => prev.map(item => 
      item.id === id ? { ...item, managementComment: comment } : item
    ));
  }, []);

  const addUploadedDocuments = useCallback((docs: UploadedDocument[]) => {
    setUploadedDocuments(prev => [...prev, ...docs]);
  }, []);

  const removeUploadedDocument = useCallback((id: string) => {
    setUploadedDocuments(prev => prev.filter(d => d.id !== id));
  }, []);

  const processUploadedFiles = useCallback((files: File[], source: 'databook' | 'rfi-tracker') => {
    const newDocs: UploadedDocument[] = files.map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: file.name,
      size: file.size,
      type: file.type,
      uploadedAt: new Date(),
      source,
    }));
    addUploadedDocuments(newDocs);

    // When documents are uploaded from databook, update RFI items that might be fulfilled
    // This is a simulation - in real implementation, you'd parse the files
    if (source === 'databook') {
      // Mark relevant RFI items as "In Progress" when related docs are uploaded
      const fileNameLower = files.map(f => f.name.toLowerCase());
      
      setRFIItems(prev => prev.map(item => {
        // Check if any uploaded file might relate to this RFI item
        const itemAreaLower = item.area.toLowerCase();
        const hasRelatedDoc = fileNameLower.some(name => 
          name.includes('trial') && itemAreaLower.includes('trial') ||
          name.includes('management') && itemAreaLower.includes('management') ||
          name.includes('legal') && itemAreaLower.includes('legal') ||
          name.includes('accounting') && itemAreaLower.includes('accounting') ||
          name.includes('earnings') && itemAreaLower.includes('earnings')
        );
        
        if (hasRelatedDoc && item.status === 'Open') {
          return { ...item, status: 'In Progress' as RFIStatus };
        }
        return item;
      }));
    }
  }, [addUploadedDocuments]);

  const setTransactionBackground = useCallback((data: TransactionBackground) => {
    setTransactionBackgroundState(data);
  }, []);

  return (
    <RFIContext.Provider value={{
      rfiItems,
      newItemIds,
      uploadedDocuments,
      transactionBackground,
      addRFIItems,
      updateRFIStatus,
      updateRFIComment,
      addUploadedDocuments,
      removeUploadedDocument,
      processUploadedFiles,
      setTransactionBackground,
      markItemSeen,
    }}>
      {children}
    </RFIContext.Provider>
  );
};
